import React, { Component, createRef } from "react";
import { Link } from "react-router-dom";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { withRouterHooks } from "../../../withRouters/withRoutersHook";
import axios from "../../../Actions/Axios";
import DropdownField from "../FileManager/DropdownField";
import {
  SavePDFTemplateFieldsAction,
  SetActivePDFTemplateAction,
} from "../../../Actions/Forms";
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Save,
  Trash2,
  Loader2,
  CheckCircle,
  AlertCircle,
  X,
  Pencil,
  Wand2,
  Layers,
} from "lucide-react";

const FIELD_TYPES = ["text", "date", "number", "boolean", "signature"];

class PDFTemplateMapper extends Component {
  static propTypes = {
    activePDFTemplate: PropTypes.object,
    SavePDFTemplateFieldsAction: PropTypes.func.isRequired,
    SetActivePDFTemplateAction: PropTypes.func.isRequired,
    navigate: PropTypes.func.isRequired,
    params: PropTypes.object,
  };

  state = {
    pageImage: null,
    currentPage: 0,
    pageCount: 1,
    loadingPage: false,
    fields: [],
    // Drag state
    isDrawing: false,
    drawStart: null,
    drawCurrent: null,
    // Field name modal
    showFieldModal: false,
    pendingBox: null,
    newFieldName: "",
    newFieldType: "text",
    // Save state
    saving: false,
    saveSuccess: false,
    autoSaved: false,
    saveError: null,
    // Auto-detect state
    autoDetecting: false,
    autoDetectError: null,
    // Inline rename state (sidebar)
    editingFieldId: null,
    editingFieldName: "",
    editingFieldType: "text",
    editingCellCount: "",
    // Floating panel (canvas click)
    floatingFieldId: null,
    floatingFieldName: "",
    floatingFieldType: "text",
    floatingCellCount: "",
    floatingPos: null, // { x, y } in px relative to canvas container
    // Image natural dimensions
    imgNaturalW: 0,
    imgNaturalH: 0,
    imgRenderedW: 0,
    imgRenderedH: 0,
    // Grouping
    groupMode: false,
    selectedFieldIds: [],
    showGroupModal: false,
    groupName: "",
    groupSelection: "single",
    // Sidebar group editing
    editingGroupId: null,
    editingGroupName: "",
    editingGroupItemId: null,
    editingGroupItemName: "",
    editingGroupItemType: "text",
    editingGroupItemCellCount: "",
    addingToGroupId: null,
    addingSelectedFieldIds: [],
  };

  containerRef = createRef();
  imgRef = createRef();
  sidebarFieldRefs = {};

  componentDidMount() {
    window.addEventListener("mouseup", this.handleGlobalMouseUp);

    const { activePDFTemplate, params } = this.props;
    if (activePDFTemplate) {
      this.setState({ fields: activePDFTemplate.fields || [] }, () => {
        this.loadPage(0);
      });
    } else {
      const id = params?.id;
      if (id) {
        axios
          .get(`/api/form/pdf-templates/${id}/`)
          .then((res) => {
            this.props.SetActivePDFTemplateAction(res.data);
            this.setState({ fields: res.data.fields || [] }, () => {
              this.loadPage(0);
            });
          })
          .catch(() => this.props.navigate("/dashboard/smartflow"));
      }
    }

    this.resizeObserver = new ResizeObserver(() => {
      if (this.imgRef.current) {
        this.setState({
          imgRenderedW: this.imgRef.current.offsetWidth,
          imgRenderedH: this.imgRef.current.offsetHeight,
        });
      }
    });
  }

  componentWillUnmount() {
    window.removeEventListener("mouseup", this.handleGlobalMouseUp);
    if (this.resizeObserver) this.resizeObserver.disconnect();
  }

  handleGlobalMouseUp = () => {
    if (this.state.isDrawing) {
      this.setState({ isDrawing: false, drawStart: null, drawCurrent: null });
    }
  };

  loadPage = async (page) => {
    const { activePDFTemplate, params } = this.props;
    const id = activePDFTemplate?.id || params?.id;
    if (!id) return;
    this.setState({ loadingPage: true, pageImage: null });
    try {
      const res = await axios.get(`/api/form/pdf-templates/${id}/pages/${page}/`);
      this.setState({ pageImage: res.data.image, currentPage: page, loadingPage: false });
    } catch {
      this.setState({ loadingPage: false });
    }
  };

  handleImgLoad = () => {
    if (!this.imgRef.current) return;
    this.setState({
      imgNaturalW: this.imgRef.current.naturalWidth,
      imgNaturalH: this.imgRef.current.naturalHeight,
      imgRenderedW: this.imgRef.current.offsetWidth,
      imgRenderedH: this.imgRef.current.offsetHeight,
    });
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver.observe(this.imgRef.current);
    }
  };

  getRelativeCoords = (e) => {
    const rect = this.imgRef.current.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left) / rect.width,
      y: (e.clientY - rect.top) / rect.height,
    };
  };

  handleMouseDown = (e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    const coords = this.getRelativeCoords(e);
    this.setState({ isDrawing: true, drawStart: coords, drawCurrent: coords });
  };

  handleMouseMove = (e) => {
    if (!this.state.isDrawing) return;
    const coords = this.getRelativeCoords(e);
    this.setState({ drawCurrent: coords });
  };

  handleMouseUp = (e) => {
    if (!this.state.isDrawing) return;
    const { drawStart } = this.state;
    const end = this.getRelativeCoords(e);

    const x = Math.min(drawStart.x, end.x);
    const y = Math.min(drawStart.y, end.y);
    const width = Math.abs(end.x - drawStart.x);
    const height = Math.abs(end.y - drawStart.y);

    if (width > 0.01 && height > 0.01) {
      this.setState({
        isDrawing: false,
        drawStart: null,
        drawCurrent: null,
        showFieldModal: true,
        pendingBox: { x, y, width, height, page: this.state.currentPage + 1 },
        newFieldName: "",
        newFieldType: "text",
      });
    } else {
      this.setState({ isDrawing: false, drawStart: null, drawCurrent: null });
    }
  };

  handleAddField = () => {
    const { pendingBox, newFieldName, newFieldType, fields } = this.state;
    if (!newFieldName.trim()) return;

    const newField = {
      id: Date.now().toString(),
      name: newFieldName.trim(),
      type: newFieldType,
      ...pendingBox,
    };

    this.setState({
      fields: [...fields, newField],
      showFieldModal: false,
      pendingBox: null,
      newFieldName: "",
    }, () => this.handleSave(true));
  };

  handleDeleteField = (fieldId) => {
    this.setState((prev) => ({
      fields: prev.fields.filter((f) => f.id !== fieldId),
      editingFieldId: prev.editingFieldId === fieldId ? null : prev.editingFieldId,
    }), () => this.handleSave(true));
  };

  handleStartEdit = (field) => {
    this.setState({
      editingFieldId: field.id,
      editingFieldName: field.name,
      editingFieldType: field.type,
      editingCellCount: field.cell_count != null ? String(field.cell_count) : "",
    }, () => {
      const el = this.sidebarFieldRefs[field.id];
      if (el) el.scrollIntoView({ behavior: "smooth", block: "nearest" });
    });
  };

  handleCommitEdit = () => {
    const { editingFieldId, editingFieldName, editingFieldType, editingCellCount, fields } = this.state;
    if (!editingFieldId || !editingFieldName.trim()) {
      this.setState({ editingFieldId: null });
      return;
    }
    const cellCount = parseInt(editingCellCount, 10);
    this.setState({
      fields: fields.map((f) => {
        if (f.id !== editingFieldId) return f;
        const updated = { ...f, name: editingFieldName.trim(), type: editingFieldType };
        if (cellCount > 0) updated.cell_count = cellCount;
        else delete updated.cell_count;
        return updated;
      }),
      editingFieldId: null,
    }, () => this.handleSave(true));
  };

  handleOpenFloating = (field, e) => {
    e.stopPropagation();
    if (!this.imgRef.current) return;
    const rect = this.imgRef.current.getBoundingClientRect();
    const containerRect = this.containerRef.current.getBoundingClientRect();
    // Position panel just below the field box, relative to container
    const boxBottom = (field.y + field.height) * rect.height;
    const boxLeft = field.x * rect.width;
    const relX = rect.left - containerRect.left + boxLeft;
    const relY = rect.top - containerRect.top + boxBottom + 6;
    this.setState({
      floatingFieldId: field.id,
      floatingFieldName: field.name,
      floatingFieldType: field.type || "text",
      floatingCellCount: field.cell_count != null ? String(field.cell_count) : "",
      floatingPos: { x: relX, y: relY },
      editingFieldId: null,
    });
  };

  handleCommitFloating = () => {
    const { floatingFieldId, floatingFieldName, floatingFieldType, floatingCellCount, fields } = this.state;
    if (!floatingFieldId || !floatingFieldName.trim()) {
      this.setState({ floatingFieldId: null });
      return;
    }
    const cellCount = parseInt(floatingCellCount, 10);
    this.setState({
      fields: fields.map((f) => {
        if (f.id !== floatingFieldId) return f;
        const updated = { ...f, name: floatingFieldName.trim(), type: floatingFieldType };
        if (cellCount > 0) updated.cell_count = cellCount;
        else delete updated.cell_count;
        return updated;
      }),
      floatingFieldId: null,
    }, () => this.handleSave(true));
  };

  handleAutoDetect = async () => {
    const { activePDFTemplate, params } = this.props;
    const id = activePDFTemplate?.id || params?.id;
    if (!id) return;

    this.setState({ autoDetecting: true, autoDetectError: null });
    try {
      const res = await axios.get(
        `/api/form/pdf-templates/${id}/pages/${this.state.currentPage}/detect-fields/`
      );
      const boxes = res.data.boxes || [];
      if (boxes.length === 0) {
        this.setState({ autoDetecting: false, autoDetectError: "No fields detected on this page." });
        setTimeout(() => this.setState({ autoDetectError: null }), 3000);
        return;
      }

      const currentPageNumber = this.state.currentPage + 1;
      const existingPageFields = this.state.fields.filter(
        (f) => f.page === currentPageNumber
      );
      const existingNames = new Set(existingPageFields.map((f) => f.name).filter(Boolean));
      const existingGroupIds = new Set(existingPageFields.map((f) => f.id).filter(Boolean));

      const getIou = (a, b) => {
        if (a.x == null || b.x == null) return 0;
        const ax2 = a.x + a.width;
        const ay2 = a.y + a.height;
        const bx2 = b.x + b.width;
        const by2 = b.y + b.height;
        const ix1 = Math.max(a.x, b.x);
        const iy1 = Math.max(a.y, b.y);
        const ix2 = Math.min(ax2, bx2);
        const iy2 = Math.min(ay2, by2);
        if (ix2 <= ix1 || iy2 <= iy1) return 0;
        const interArea = (ix2 - ix1) * (iy2 - iy1);
        const aArea = a.width * a.height;
        const bArea = b.width * b.height;
        return interArea / (aArea + bArea - interArea);
      };

      // Skip boxes already present: match by name, by group id, or by coordinate overlap.
      const uniqueBoxes = boxes.filter((box) => {
        if (box.name && existingNames.has(box.name)) return false;
        if (box.type === "group" && existingGroupIds.has(box.id)) return false;
        if (box.type === "group_option" && existingGroupIds.has(box.groupId)) return false;
        return !existingPageFields.some((field) => getIou(box, field) > 0.5);
      });

      if (uniqueBoxes.length === 0) {
        this.setState({
          autoDetecting: false,
          autoDetectError: "No new fields found. Existing detected fields were kept.",
        });
        setTimeout(() => this.setState({ autoDetectError: null }), 3000);
        return;
      }

      const newFields = uniqueBoxes.flatMap((box, i) => {
        if (box.type === "group") {
          // Group container — already has id, options, selection from backend
          const group = {
            ...box,
            id: box.id || `auto_group_${Date.now()}_${i}`,
            page: currentPageNumber,
          };
          // group_option entries embedded in uniqueBoxes will be handled below
          return [group];
        }
        if (box.type === "group_option") {
          // group_option — already has id, groupId, coordinates from backend
          return [{ ...box, page: currentPageNumber }];
        }
        // Regular field
        return [{
          id: `auto_${Date.now()}_${i}`,
          name: box.name || `field_${this.state.fields.length + i + 1}`,
          label: box.label || box.name || `field_${this.state.fields.length + i + 1}`,
          type: box.type || "text",
          page: currentPageNumber,
          x: box.x,
          y: box.y,
          width: box.width,
          height: box.height,
          ...(box.cell_count ? { cell_count: box.cell_count } : {}),
          ...(box.date_format ? { date_format: box.date_format } : {}),
        }];
      });

      this.setState((prev) => {
        const combined = [...prev.fields, ...newFields];

        // Build y positions for groups from their first option
        const groupY = {};
        combined.forEach((f) => {
          if (f.type === "group_option" && f.groupId && !(f.groupId in groupY)) {
            groupY[f.groupId] = { page: f.page || 1, y: f.y ?? 0, x: f.x ?? 0 };
          }
        });

        // Separate top-level (non-option) and options
        const topLevel = combined.filter((f) => f.type !== "group_option");
        const optionsByGroup = {};
        combined.forEach((f) => {
          if (f.type === "group_option") {
            const gid = f.groupId;
            if (!optionsByGroup[gid]) optionsByGroup[gid] = [];
            optionsByGroup[gid].push(f);
          }
        });

        // Sort each group's options left-to-right
        Object.values(optionsByGroup).forEach((opts) =>
          opts.sort((a, b) => (a.y ?? 0) - (b.y ?? 0) || (a.x ?? 0) - (b.x ?? 0))
        );

        // Sort top-level by (page, y, x), using group's first-option position for groups
        topLevel.sort((a, b) => {
          const posA = a.type === "group" ? groupY[a.id] || { page: a.page || 1, y: 0, x: 0 } : { page: a.page || 1, y: a.y ?? 0, x: a.x ?? 0 };
          const posB = b.type === "group" ? groupY[b.id] || { page: b.page || 1, y: 0, x: 0 } : { page: b.page || 1, y: b.y ?? 0, x: b.x ?? 0 };
          if (posA.page !== posB.page) return posA.page - posB.page;
          if (Math.abs(posA.y - posB.y) > 0.01) return posA.y - posB.y;
          return posA.x - posB.x;
        });

        // Reconstruct: each group immediately followed by its options
        const ordered = [];
        topLevel.forEach((f) => {
          ordered.push(f);
          if (f.type === "group") ordered.push(...(optionsByGroup[f.id] || []));
        });

        return { fields: ordered, autoDetecting: false };
      }, () => this.handleSave(true));
    } catch (e) {
      this.setState({ autoDetecting: false, autoDetectError: "Auto-detect failed. Try again." });
      setTimeout(() => this.setState({ autoDetectError: null }), 3000);
    }
  };

  handleFieldClick = (field, e) => {
    const { groupMode, selectedFieldIds, addingToGroupId, addingSelectedFieldIds } = this.state;
    if (addingToGroupId) {
      e.stopPropagation();
      if (field.type === "group_option" || field.type === "group") return;
      const already = addingSelectedFieldIds.includes(field.id);
      this.setState({
        addingSelectedFieldIds: already
          ? addingSelectedFieldIds.filter((id) => id !== field.id)
          : [...addingSelectedFieldIds, field.id],
        floatingFieldId: null,
      });
      return;
    }
    // In group mode OR shift+click: toggle selection instead of opening floating panel
    if (groupMode || e.shiftKey) {
      e.stopPropagation();
      // Don't allow selecting group containers themselves
      if (field.type === "group") return;
      const already = selectedFieldIds.includes(field.id);
      this.setState({
        selectedFieldIds: already
          ? selectedFieldIds.filter((id) => id !== field.id)
          : [...selectedFieldIds, field.id],
        floatingFieldId: null,
      });
      return;
    }
    this.handleOpenFloating(field, e);
  };

  handleCreateGroup = () => {
    const { selectedFieldIds, groupName, groupSelection, fields } = this.state;
    if (!groupName.trim() || selectedFieldIds.length < 2) return;

    // Build options from selected fields, preserving their label as the field name
    const options = selectedFieldIds.map((fid) => {
      const f = fields.find((x) => x.id === fid);
      return { fieldId: fid, label: f?.name || fid };
    });

    // Get page from first selected field
    const firstField = fields.find((f) => f.id === selectedFieldIds[0]);
    const page = firstField?.page || this.state.currentPage + 1;

    const group = {
      id: `group_${Date.now()}`,
      name: groupName.trim(),
      type: "group",
      selection: groupSelection,
      page,
      options,
    };

    // Mark selected fields as group_option so they render differently
    const updatedFields = fields.map((f) =>
      selectedFieldIds.includes(f.id)
        ? { ...f, type: "group_option", groupId: group.id }
        : f
    );

    this.setState({
      fields: [...updatedFields, group],
      showGroupModal: false,
      groupName: "",
      groupSelection: "single",
      selectedFieldIds: [],
      groupMode: false,
    }, () => this.handleSave(true));
  };

  handleDeleteGroup = (groupId) => {
    this.setState((prev) => ({
      // Restore group_option fields back to plain text fields, remove the group
      fields: prev.fields
        .filter((f) => f.id !== groupId)
        .map((f) => f.groupId === groupId ? { ...f, type: "text", groupId: undefined } : f),
    }), () => this.handleSave(true));
  };

  handleStartEditGroup = (group) => {
    this.setState({
      editingGroupId: group.id,
      editingGroupName: group.name || "",
    });
  };

  handleCommitEditGroup = () => {
    const { editingGroupId, editingGroupName, fields } = this.state;
    if (!editingGroupId) return;

    const trimmedName = editingGroupName.trim();
    if (!trimmedName) {
      this.setState({ editingGroupId: null, editingGroupName: "" });
      return;
    }

    this.setState({
      fields: fields.map((f) =>
        f.id === editingGroupId ? { ...f, name: trimmedName } : f
      ),
      editingGroupId: null,
      editingGroupName: "",
    }, () => this.handleSave(true));
  };

  handleStartEditGroupItem = (field) => {
    this.setState({
      editingGroupItemId: field.id,
      editingGroupItemName: field.name || "",
      editingGroupItemType: field.type || "text",
      editingGroupItemCellCount: field.cell_count != null ? String(field.cell_count) : "",
    });
  };

  handleCommitEditGroupItem = (groupId) => {
    const {
      editingGroupItemId,
      editingGroupItemName,
      editingGroupItemType,
      editingGroupItemCellCount,
      fields,
    } = this.state;
    if (!editingGroupItemId) return;

    const trimmedLabel = editingGroupItemName.trim();
    if (!trimmedLabel) {
      this.setState({
        editingGroupItemId: null,
        editingGroupItemName: "",
        editingGroupItemType: "text",
        editingGroupItemCellCount: "",
      });
      return;
    }
    const cellCount = parseInt(editingGroupItemCellCount, 10);

    this.setState({
      fields: fields.map((f) => {
        if (f.id === editingGroupItemId) {
          const updated = { ...f, name: trimmedLabel, type: editingGroupItemType };
          if (cellCount > 0) updated.cell_count = cellCount;
          else delete updated.cell_count;
          return updated;
        }
        if (f.id === groupId && f.type === "group") {
          return {
            ...f,
            options: (f.options || []).map((opt) =>
              opt.fieldId === editingGroupItemId
                ? { ...opt, label: trimmedLabel }
                : opt
            ),
          };
        }
        return f;
      }),
      editingGroupItemId: null,
      editingGroupItemName: "",
      editingGroupItemType: "text",
      editingGroupItemCellCount: "",
    }, () => this.handleSave(true));
  };

  handleAddFieldToGroup = (groupId, fieldIds) => {
    const { fields } = this.state;
    const ids = Array.isArray(fieldIds) ? fieldIds : [fieldIds];
    const validFieldIds = ids.filter(Boolean);
    if (validFieldIds.length === 0) return;

    const selectedFields = validFieldIds
      .map((id) => fields.find((f) => f.id === id))
      .filter((f) => f && f.type !== "group" && f.type !== "group_option");
    if (selectedFields.length === 0) return;
    const selectedSet = new Set(selectedFields.map((f) => f.id));

    this.setState({
      fields: fields.map((f) => {
        if (selectedSet.has(f.id)) {
          return { ...f, type: "group_option", groupId };
        }
        if (f.id === groupId && f.type === "group") {
          const existing = new Set((f.options || []).map((opt) => opt.fieldId));
          const newOptions = selectedFields
            .filter((sf) => !existing.has(sf.id))
            .map((sf) => ({ fieldId: sf.id, label: sf.name || sf.id }));
          return {
            ...f,
            options: [...(f.options || []), ...newOptions],
          };
        }
        return f;
      }),
      addingSelectedFieldIds: [],
    }, () => this.handleSave(true));
  };

  handleSave = async (isAuto = false) => {
    const { activePDFTemplate, params } = this.props;
    const id = activePDFTemplate?.id || params?.id;
    if (!id) return;

    this.setState({ saving: true, saveError: null, saveSuccess: false, autoSaved: false });
    try {
      await this.props.SavePDFTemplateFieldsAction(id, this.state.fields);
      this.setState({ saving: false, ...(isAuto ? { autoSaved: true } : { saveSuccess: true }) });
      setTimeout(() => this.setState({ saveSuccess: false, autoSaved: false }), 2500);
    } catch {
      this.setState({ saving: false, saveError: "Failed to save. Please try again." });
    }
  };

  renderDrawingOverlay = () => {
    const { isDrawing, drawStart, drawCurrent } = this.state;
    if (!isDrawing || !drawStart || !drawCurrent || !this.imgRef.current) return null;

    const rect = this.imgRef.current.getBoundingClientRect();
    const x = Math.min(drawStart.x, drawCurrent.x) * rect.width;
    const y = Math.min(drawStart.y, drawCurrent.y) * rect.height;
    const width = Math.abs(drawCurrent.x - drawStart.x) * rect.width;
    const height = Math.abs(drawCurrent.y - drawStart.y) * rect.height;

    return (
      <div
        className="absolute border-2 border-blue-500 bg-blue-400/10 pointer-events-none"
        style={{ left: x, top: y, width, height }}
      />
    );
  };

  renderFieldBoxes = () => {
    const { fields, currentPage, floatingFieldId, selectedFieldIds, groupMode, addingToGroupId, addingSelectedFieldIds } = this.state;
    if (!this.imgRef.current) return null;

    const rect = this.imgRef.current.getBoundingClientRect();
    // Render all non-group fields on current page (groups have no direct box)
    const pageFields = fields.filter((f) => f.page === currentPage + 1 && f.type !== "group");

    return pageFields.map((field) => {
      const isActive = floatingFieldId === field.id;
      const isSelected = selectedFieldIds.includes(field.id);
      const isGroupOption = field.type === "group_option";
      const isQueuedForAdd = addingSelectedFieldIds.includes(field.id);
      let borderColor, bgColor, labelBg;
      if (isQueuedForAdd || isSelected) {
        borderColor = "border-amber-500";
        bgColor = "bg-amber-400/20";
        labelBg = "bg-amber-500";
      } else if (isGroupOption) {
        borderColor = "border-orange-400";
        bgColor = "bg-orange-400/10";
        labelBg = "bg-orange-400";
      } else if (isActive) {
        borderColor = "border-blue-500";
        bgColor = "bg-blue-400/10";
        labelBg = "bg-blue-500";
      } else {
        borderColor = "border-emerald-500";
        bgColor = "bg-emerald-400/10";
        labelBg = "bg-emerald-500";
      }

      return (
        <div
          key={field.id}
          className={`absolute border-2 group cursor-pointer ${borderColor} ${bgColor}`}
          style={{
            left: field.x * rect.width,
            top: field.y * rect.height,
            width: field.width * rect.width,
            height: field.height * rect.height,
          }}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => this.handleFieldClick(field, e)}
        >
          <div className={`absolute -top-5 left-0 flex items-center gap-1 ${labelBg} text-white text-xs font-inter font-medium px-1.5 py-0.5 rounded whitespace-nowrap max-w-[160px] opacity-0 group-hover:opacity-100 transition-opacity duration-150 pointer-events-none`}>
            <span className="truncate">{field.label || field.name}</span>
          </div>
        </div>
      );
    });
  };

  renderFloatingPanel = () => {
    const {
      floatingFieldId,
      floatingFieldName,
      floatingFieldType,
      floatingPos,
      fields,
    } = this.state;
    if (!floatingFieldId || !floatingPos) return null;
    const field = fields.find((f) => f.id === floatingFieldId);
    if (!field) return null;

    return (
      <div
        className="absolute z-30 bg-white rounded-xl shadow-xl border border-gray-200 p-3 w-56"
        style={{ left: floatingPos.x, top: floatingPos.y }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-2">
          <p className="font-inter font-semibold text-xs text-gray-500 uppercase tracking-wide">Edit field</p>
          <button
            onClick={() => this.setState({ floatingFieldId: null })}
            className="p-0.5 text-gray-300 hover:text-gray-500 transition-colors"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
        <div className="flex flex-col gap-2">
          <input
            autoFocus
            type="text"
            value={floatingFieldName}
            onChange={(e) => this.setState({ floatingFieldName: e.target.value })}
            onKeyDown={(e) => {
              if (e.key === "Enter") this.handleCommitFloating();
              if (e.key === "Escape") this.setState({ floatingFieldId: null });
            }}
            placeholder="Field name"
            className="w-full h-8 px-2.5 bg-gray-50 border border-gray-200 rounded-lg text-xs font-inter text-gray-900 outline-none focus:border-blue-500 transition-all"
          />
          <DropdownField
            name="floatingFieldType"
            value={floatingFieldType}
            onChange={(e) => this.setState({ floatingFieldType: e.target.value })}
            options={FIELD_TYPES.map((t) => ({ value: t, label: t }))}
          />
          <input
            type="number"
            min="0"
            value={this.state.floatingCellCount}
            onChange={(e) => this.setState({ floatingCellCount: e.target.value })}
            placeholder="Cell count (optional)"
            className="w-full h-8 px-2.5 bg-gray-50 border border-gray-200 rounded-lg text-xs font-inter text-gray-700 outline-none focus:border-blue-500 transition-all"
          />
          <div className="flex gap-1.5">
            <button
              onClick={this.handleCommitFloating}
              className="flex-1 py-1.5 bg-blue-600 text-white font-inter font-semibold text-xs rounded-lg hover:bg-blue-700 transition-colors"
            >
              Save
            </button>
            <button
              onClick={() => {
                this.handleDeleteField(floatingFieldId);
                this.setState({ floatingFieldId: null });
              }}
              className="p-1.5 text-red-400 border border-red-100 rounded-lg hover:bg-red-50 transition-colors"
              title="Delete field"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>
    );
  };

  render() {
    const {
      pageImage,
      currentPage,
      loadingPage,
      fields,
      showFieldModal,
      newFieldName,
      newFieldType,
      saving,
      saveSuccess,
      autoSaved,
      saveError,
      isDrawing,
      autoDetecting,
      autoDetectError,
      editingFieldId,
      editingFieldName,
      editingFieldType,
      editingCellCount,
      floatingFieldId,
      groupMode,
      selectedFieldIds,
      showGroupModal,
      groupName,
      groupSelection,
      editingGroupId,
      editingGroupName,
      editingGroupItemId,
      editingGroupItemName,
      editingGroupItemType,
      editingGroupItemCellCount,
      addingToGroupId,
      addingSelectedFieldIds,
    } = this.state;

    const { activePDFTemplate } = this.props;
    const groups = fields.filter((f) => f.type === "group");
    const groupedItemIds = new Set(
      groups.flatMap((g) => (g.options || []).map((opt) => opt.fieldId))
    );
    const ungroupedFields = fields.filter(
      (f) => f.type !== "group" && !groupedItemIds.has(f.id)
    );

    return (
      <div className="w-full min-h-screen bg-gray-50 flex flex-col">
        {/* Top bar */}
        <div className="sticky top-0 z-20 bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link
              to="/dashboard/smartflow"
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="font-SFPro font-semibold text-gray-900">
                {activePDFTemplate?.name || "PDF Template Mapper"}
              </h1>
              <p className="font-inter text-xs text-gray-400">
                Draw boxes on the PDF to define fillable fields
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {(saveSuccess || autoSaved) && (
              <div className="flex items-center gap-1.5 text-emerald-600 font-inter text-sm">
                <CheckCircle className="w-4 h-4" /> {autoSaved ? "Auto-saved" : "Saved"}
              </div>
            )}
            {saving && !saveSuccess && !autoSaved && (
              <div className="flex items-center gap-1.5 text-gray-400 font-inter text-sm">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving…
              </div>
            )}
            {saveError && (
              <div className="flex items-center gap-1.5 text-red-500 font-inter text-sm">
                <AlertCircle className="w-4 h-4" /> {saveError}
              </div>
            )}
            {autoDetectError && (
              <div className="flex items-center gap-1.5 text-orange-500 font-inter text-sm">
                <AlertCircle className="w-4 h-4" /> {autoDetectError}
              </div>
            )}
            {/* Group controls — shown when 2+ fields selected */}
            {selectedFieldIds.length >= 2 && (
              <button
                onClick={() => this.setState({ showGroupModal: true, groupName: "", groupSelection: "single" })}
                className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white font-inter font-semibold text-sm rounded-xl hover:bg-amber-600 transition-colors"
              >
                <Layers className="w-4 h-4" />
                Group {selectedFieldIds.length} fields
              </button>
            )}
            {selectedFieldIds.length > 0 && (
              <button
                onClick={() => this.setState({ selectedFieldIds: [], groupMode: false })}
                className="flex items-center gap-2 px-3 py-2 border border-gray-200 text-gray-500 font-inter text-sm rounded-xl hover:bg-gray-50 transition-colors"
              >
                <X className="w-3.5 h-3.5" /> Clear
              </button>
            )}
            <button
              onClick={() => this.setState((prev) => ({ groupMode: !prev.groupMode, selectedFieldIds: [] }))}
              className={`flex items-center gap-2 px-4 py-2 font-inter font-semibold text-sm rounded-xl transition-colors ${
                groupMode
                  ? "bg-amber-100 text-amber-700 border border-amber-300"
                  : "border border-gray-200 text-gray-600 hover:bg-gray-50"
              }`}
            >
              <Layers className="w-4 h-4" />
              {groupMode ? "Grouping…" : "Group mode"}
            </button>
            <button
              onClick={this.handleAutoDetect}
              disabled={autoDetecting || loadingPage || !pageImage}
              className="flex items-center gap-2 px-4 py-2 bg-violet-600 text-white font-inter font-semibold text-sm rounded-xl hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {autoDetecting ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Detecting…</>
              ) : (
                <><Wand2 className="w-4 h-4" /> Auto-detect</>
              )}
            </button>
            <button
              onClick={this.handleSave}
              disabled={saving || fields.length === 0}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white font-inter font-semibold text-sm rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</>
              ) : (
                <><Save className="w-4 h-4" /> Save Fields</>
              )}
            </button>
          </div>
        </div>

        {/* Main layout: canvas + sidebar */}
        <div className="flex flex-1 min-h-0">
          {/* PDF Canvas */}
          <div className="flex-1 flex flex-col items-center overflow-auto p-6 gap-4">
            {/* Page navigation */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => this.loadPage(currentPage - 1)}
                disabled={currentPage === 0 || loadingPage}
                className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="w-4 h-4 text-gray-500" />
              </button>
              <span className="font-inter text-sm text-gray-500">
                Page {currentPage + 1}
              </span>
              <button
                onClick={() => this.loadPage(currentPage + 1)}
                disabled={loadingPage}
                className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight className="w-4 h-4 text-gray-500" />
              </button>
            </div>

            {/* PDF image + drawing layer */}
            <div
              className="relative inline-block shadow-lg rounded-lg overflow-visible select-none"
              style={{
                cursor: addingToGroupId || groupMode ? "cell" : floatingFieldId ? "default" : "crosshair",
              }}
              ref={this.containerRef}
            >
              {loadingPage ? (
                <div className="w-[640px] h-[800px] bg-gray-100 flex items-center justify-center rounded-lg">
                  <Loader2 className="w-8 h-8 text-gray-300 animate-spin" />
                </div>
              ) : pageImage ? (
                <>
                  <img
                    ref={this.imgRef}
                    src={`data:image/png;base64,${pageImage}`}
                    alt={`Page ${currentPage + 1}`}
                    className="block max-w-[720px] w-full h-auto rounded-lg"
                    onLoad={this.handleImgLoad}
                    onMouseDown={this.handleMouseDown}
                    onMouseMove={this.handleMouseMove}
                    onMouseUp={this.handleMouseUp}
                    onClick={() => floatingFieldId && this.setState({ floatingFieldId: null })}
                    draggable={false}
                  />
                  {this.renderFieldBoxes()}
                  {this.renderDrawingOverlay()}
                  {this.renderFloatingPanel()}
                </>
              ) : (
                <div className="w-[640px] h-[800px] bg-gray-100 flex items-center justify-center text-gray-400 font-inter text-sm rounded-lg">
                  No page loaded
                </div>
              )}
            </div>
          </div>

          {/* Field list sidebar */}
          <div className="w-72 shrink-0 border-l border-gray-200 bg-white flex flex-col">
            <div className="px-4 py-3 border-b border-gray-100">
              <p className="font-inter font-semibold text-sm text-gray-700">
                Fields ({fields.length})
              </p>
            </div>
            <div className="flex-1 overflow-y-auto">
              {fields.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-48 gap-2 text-gray-300">
                  <p className="font-inter text-sm text-center px-4">
                    Draw boxes on the PDF to add fields
                  </p>
                </div>
              ) : (
                <div className="flex flex-col divide-y divide-gray-50">
                  {ungroupedFields.map((field) => (
                    <div
                      key={field.id}
                      ref={(el) => { this.sidebarFieldRefs[field.id] = el; }}
                      className={`px-3 py-2 ${
                        field.page === currentPage + 1 ? "bg-blue-50/50" : ""
                      }`}
                    >
                      {editingFieldId === field.id ? (
                        <div className="flex flex-col gap-1.5">
                          <input
                            autoFocus
                            type="text"
                            value={editingFieldName}
                            onChange={(e) => this.setState({ editingFieldName: e.target.value })}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") this.handleCommitEdit();
                              if (e.key === "Escape") this.setState({ editingFieldId: null });
                            }}
                            className="w-full h-7 px-2 bg-white border border-blue-400 rounded-lg text-xs font-inter text-gray-900 outline-none"
                          />
                          <DropdownField
                            name="editingFieldType"
                            value={editingFieldType}
                            onChange={(e) => this.setState({ editingFieldType: e.target.value })}
                            options={FIELD_TYPES.map((t) => ({ value: t, label: t }))}
                          />
                          <input
                            type="number"
                            min="0"
                            value={editingCellCount}
                            onChange={(e) => this.setState({ editingCellCount: e.target.value })}
                            placeholder="Cell count (optional)"
                            className="w-full h-7 px-2 bg-white border border-gray-200 rounded-lg text-xs font-inter text-gray-700 outline-none"
                          />
                          <div className="flex gap-1.5">
                            <button
                              onClick={this.handleCommitEdit}
                              className="flex-1 py-1 bg-blue-600 text-white font-inter text-xs rounded-lg hover:bg-blue-700 transition-colors"
                            >
                              Save
                            </button>
                            <button
                              onClick={() => this.setState({ editingFieldId: null })}
                              className="flex-1 py-1 border border-gray-200 text-gray-500 font-inter text-xs rounded-lg hover:bg-gray-50 transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between gap-1">
                          <div className="min-w-0 flex-1">
                            <p className="font-inter font-medium text-sm text-gray-800 truncate">
                              {field.label || field.name}
                            </p>
                            <p className="font-inter text-xs text-gray-400">
                              {`${field.type} · p${field.page}${field.cell_count ? ` · ${field.cell_count} cells` : ""}`}
                            </p>
                          </div>
                          <div className="flex items-center gap-0.5 shrink-0">
                            <button
                              onClick={() => this.handleStartEdit(field)}
                              className="p-1 text-gray-300 hover:text-blue-500 transition-colors"
                              title="Rename field"
                            >
                              <Pencil className="w-3 h-3" />
                            </button>
                            <button
                              onClick={() => this.handleDeleteField(field.id)}
                              className="p-1 text-gray-300 hover:text-red-400 transition-colors"
                              title="Delete"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                  {groups.map((group) => {
                    const items = (group.options || [])
                      .map((opt) => fields.find((f) => f.id === opt.fieldId))
                      .filter(Boolean);
                    return (
                      <div
                        key={group.id}
                        ref={(el) => { this.sidebarFieldRefs[group.id] = el; }}
                        className={`px-3 py-2 ${
                          group.page === currentPage + 1 ? "bg-blue-50/50" : ""
                        }`}
                      >
                        {editingGroupId === group.id ? (
                          <div className="flex flex-col gap-1.5">
                            <input
                              autoFocus
                              type="text"
                              value={editingGroupName}
                              onChange={(e) => this.setState({ editingGroupName: e.target.value })}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") this.handleCommitEditGroup();
                                if (e.key === "Escape") this.setState({ editingGroupId: null, editingGroupName: "" });
                              }}
                              className="w-full h-7 px-2 bg-white border border-blue-400 rounded-lg text-xs font-inter text-gray-900 outline-none"
                            />
                            <div className="flex gap-1.5">
                              <button
                                onClick={this.handleCommitEditGroup}
                                className="flex-1 py-1 bg-blue-600 text-white font-inter text-xs rounded-lg hover:bg-blue-700 transition-colors"
                              >
                                Save
                              </button>
                              <button
                                onClick={() => this.setState({ editingGroupId: null, editingGroupName: "" })}
                                className="flex-1 py-1 border border-gray-200 text-gray-500 font-inter text-xs rounded-lg hover:bg-gray-50 transition-colors"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center justify-between gap-1">
                            <div className="min-w-0 flex-1">
                              <p className="font-inter font-semibold text-sm text-gray-800 truncate">
                                {group.name}
                              </p>
                              <p className="font-inter text-xs text-gray-400">
                                {`${group.selection} select · ${group.options?.length || 0} options · p${group.page}`}
                              </p>
                            </div>
                            <div className="flex items-center gap-0.5 shrink-0">
                              <button
                                onClick={() => this.handleStartEditGroup(group)}
                                className="p-1 text-gray-300 hover:text-blue-500 transition-colors"
                                title="Rename group"
                              >
                                <Pencil className="w-3 h-3" />
                              </button>
                              <button
                                onClick={() => this.handleDeleteGroup(group.id)}
                                className="p-1 text-gray-300 hover:text-red-400 transition-colors"
                                title="Delete group"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                        )}

                        <div className="mt-2 pl-4 border-l border-gray-200 flex flex-col gap-1.5">
                          {items.map((item) => (
                            <div key={item.id} className="flex items-center justify-between gap-2">
                              {editingGroupItemId === item.id ? (
                                <div className="flex flex-1 flex-col gap-1.5">
                                  <input
                                    autoFocus
                                    type="text"
                                    value={editingGroupItemName}
                                    onChange={(e) => this.setState({ editingGroupItemName: e.target.value })}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter") this.handleCommitEditGroupItem(group.id);
                                      if (e.key === "Escape") this.setState({
                                        editingGroupItemId: null,
                                        editingGroupItemName: "",
                                        editingGroupItemType: "text",
                                        editingGroupItemCellCount: "",
                                      });
                                    }}
                                    className="flex-1 h-7 px-2 bg-white border border-blue-400 rounded-lg text-xs font-inter text-gray-900 outline-none"
                                  />
                                  <DropdownField
                                    name="editingGroupItemType"
                                    value={editingGroupItemType}
                                    onChange={(e) => this.setState({ editingGroupItemType: e.target.value })}
                                    options={FIELD_TYPES.map((t) => ({ value: t, label: t }))}
                                  />
                                  <input
                                    type="number"
                                    min="0"
                                    value={editingGroupItemCellCount}
                                    onChange={(e) => this.setState({ editingGroupItemCellCount: e.target.value })}
                                    placeholder="Cell count (optional)"
                                    className="w-full h-7 px-2 bg-white border border-gray-200 rounded-lg text-xs font-inter text-gray-700 outline-none"
                                  />
                                  <div className="flex items-center gap-1.5">
                                    <button
                                      onClick={() => this.handleCommitEditGroupItem(group.id)}
                                      className="px-2 py-1 bg-blue-600 text-white font-inter text-xs rounded-lg hover:bg-blue-700 transition-colors"
                                    >
                                      Save
                                    </button>
                                    <button
                                      onClick={() => this.setState({
                                        editingGroupItemId: null,
                                        editingGroupItemName: "",
                                        editingGroupItemType: "text",
                                        editingGroupItemCellCount: "",
                                      })}
                                      className="px-2 py-1 border border-gray-200 text-gray-500 font-inter text-xs rounded-lg hover:bg-gray-50 transition-colors"
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <>
                                  <p className="font-inter text-xs text-gray-700 truncate">
                                    {item.name}
                                  </p>
                                  <button
                                    onClick={() => this.handleStartEditGroupItem(item)}
                                    className="p-1 text-gray-300 hover:text-blue-500 transition-colors"
                                    title="Rename group item"
                                  >
                                    <Pencil className="w-3 h-3" />
                                  </button>
                                </>
                              )}
                            </div>
                          ))}
                          {addingToGroupId === group.id ? (
                            <div className="mt-1 flex items-center justify-between gap-2">
                              <p className="text-[11px] font-inter text-amber-700">
                                Select boxes on PDF ({addingSelectedFieldIds.length})
                              </p>
                              <div className="flex items-center gap-1.5">
                                <button
                                  onClick={() => this.handleAddFieldToGroup(group.id, addingSelectedFieldIds)}
                                  disabled={addingSelectedFieldIds.length === 0}
                                  className="px-2 py-1 bg-amber-500 text-white font-inter text-xs rounded-lg hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                  Add selected
                                </button>
                                <button
                                  onClick={() => this.setState({ addingSelectedFieldIds: [] })}
                                  className="px-2 py-1 border border-gray-200 text-gray-500 font-inter text-xs rounded-lg hover:bg-gray-50 transition-colors"
                                >
                                  Clear
                                </button>
                                <button
                                  onClick={() => this.setState({ addingToGroupId: null, addingSelectedFieldIds: [] })}
                                  className="px-2 py-1 border border-gray-200 text-gray-500 font-inter text-xs rounded-lg hover:bg-gray-50 transition-colors"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <button
                              onClick={() => this.setState({ addingToGroupId: group.id, addingSelectedFieldIds: [] })}
                              className="mt-1 self-start text-xs font-inter text-amber-600 hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                              disabled={ungroupedFields.length === 0}
                            >
                              + Add item to group
                            </button>
                          )}
                          {ungroupedFields.length === 0 && addingToGroupId !== group.id && (
                            <p className="text-[11px] font-inter text-gray-400">No ungrouped fields available.</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            {fields.length > 0 && (
              <div className="p-4 border-t border-gray-100">
                <button
                  onClick={() =>
                    this.props.navigate(
                      `/dashboard/smartflow/${activePDFTemplate?.id}/fill`
                    )
                  }
                  className="w-full py-2 bg-emerald-600 text-white font-inter font-semibold text-sm rounded-xl hover:bg-emerald-700 transition-colors"
                >
                  Fill This Template
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Group modal */}
        {showGroupModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm mx-4 p-6">
              <h2 className="font-SFPro font-bold text-lg text-gray-900 mb-1">Create Field Group</h2>
              <p className="font-inter text-sm text-gray-400 mb-4">
                Grouping {selectedFieldIds.length} fields as a selection question
              </p>
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-1.5">
                  <label className="font-inter font-medium text-sm text-gray-700">Group name</label>
                  <input
                    type="text"
                    value={groupName}
                    onChange={(e) => this.setState({ groupName: e.target.value })}
                    placeholder="e.g. card_type, account_type"
                    autoFocus
                    onKeyDown={(e) => e.key === "Enter" && this.handleCreateGroup()}
                    className="w-full h-10 px-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-inter text-gray-900 outline-none focus:border-blue-500 transition-all"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="font-inter font-medium text-sm text-gray-700">Selection type</label>
                  <div className="flex gap-2">
                    {["single", "multi"].map((type) => (
                      <button
                        key={type}
                        onClick={() => this.setState({ groupSelection: type })}
                        className={`flex-1 py-2 rounded-xl font-inter font-medium text-sm border transition-colors ${
                          groupSelection === type
                            ? "bg-blue-600 text-white border-blue-600"
                            : "border-gray-200 text-gray-600 hover:bg-gray-50"
                        }`}
                      >
                        {type === "single" ? "Single select" : "Multi select"}
                      </button>
                    ))}
                  </div>
                  <p className="font-inter text-xs text-gray-400">
                    {groupSelection === "single"
                      ? "Only one option can be chosen (radio)"
                      : "Multiple options can be chosen (checkboxes)"}
                  </p>
                </div>
                <div className="flex flex-col gap-1">
                  <label className="font-inter font-medium text-xs text-gray-500 uppercase tracking-wide">Options (edit labels)</label>
                  {selectedFieldIds.map((fid) => {
                    const f = fields.find((x) => x.id === fid);
                    return (
                      <div key={fid} className="flex items-center gap-2 px-2 py-1 bg-amber-50 rounded-lg border border-amber-100">
                        <span className="w-2 h-2 rounded-full bg-amber-400 shrink-0" />
                        <span className="font-inter text-xs text-gray-700 truncate">{f?.name || fid}</span>
                      </div>
                    );
                  })}
                </div>
                <div className="flex gap-3 pt-1">
                  <button
                    onClick={() => this.setState({ showGroupModal: false })}
                    className="flex-1 py-2 border border-gray-200 text-gray-600 font-inter font-medium text-sm rounded-xl hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={this.handleCreateGroup}
                    disabled={!groupName.trim()}
                    className="flex-1 py-2 bg-amber-500 text-white font-inter font-semibold text-sm rounded-xl hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Create Group
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Add field modal */}
        {showFieldModal && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
            onMouseDown={(e) => e.stopPropagation()}
            onMouseUp={(e) => e.stopPropagation()}
          >
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm mx-4 p-6">
              <h2 className="font-SFPro font-bold text-lg text-gray-900 mb-4">Name this field</h2>
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-1.5">
                  <label className="font-inter font-medium text-sm text-gray-700">Field name</label>
                  <input
                    type="text"
                    value={newFieldName}
                    onChange={(e) => this.setState({ newFieldName: e.target.value })}
                    placeholder="e.g. full_name, date_of_birth"
                    autoFocus
                    onKeyDown={(e) => e.key === "Enter" && this.handleAddField()}
                    className="w-full h-10 px-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-inter text-gray-900 outline-none focus:border-blue-500 transition-all"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="font-inter font-medium text-sm text-gray-700">Field type</label>
                  <DropdownField
                    name="newFieldType"
                    value={newFieldType}
                    onChange={(e) => this.setState({ newFieldType: e.target.value })}
                    options={FIELD_TYPES.map((t) => ({ value: t, label: t }))}
                  />
                </div>
                <div className="flex gap-3 pt-1">
                  <button
                    onClick={() => this.setState({ showFieldModal: false, pendingBox: null })}
                    className="flex-1 py-2 border border-gray-200 text-gray-600 font-inter font-medium text-sm rounded-xl hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={this.handleAddField}
                    disabled={!newFieldName.trim()}
                    className="flex-1 py-2 bg-blue-600 text-white font-inter font-semibold text-sm rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Add Field
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  activePDFTemplate: state.forms.activePDFTemplate,
});

export default withRouterHooks(
  connect(mapStateToProps, {
    SavePDFTemplateFieldsAction,
    SetActivePDFTemplateAction,
  })(PDFTemplateMapper)
);
